import * as React from 'react';

import { useDebounceFn, useRequest } from 'ahooks';
import { Col, Form, Input, Row } from 'antd';

import { PrimitiveTreeSelect, RichTextEditor } from 'components';
import { useTagAction, useTagValue } from 'context';
import { getCategories } from 'services';
import { TreeSelectSource } from 'types';
import {
  findTreeElementByKey,
  generateTreeSelectData,
  inputRule,
  normalizeToString,
} from 'utils';

type FieldType = Pick<
  ReturnType<typeof useTagValue>,
  'title' | 'slug' | 'description' | 'categories'
>;

const TagBaseInfo = () => {
  const { data: categoriesData, loading: categoriesLoading } = useRequest(
    getCategories,
    {
      cacheKey: 'categories',
    },
  );
  const { title, slug, description, ownerType } = useTagValue();
  const action = useTagAction();

  const { run: debounceUpdateTitle } = useDebounceFn(
    (debouncedTitle: typeof title) => {
      action({
        type: 'UPDATE_TAG_TITLE',
        payload: {
          title: debouncedTitle,
        },
      });
    },
  );
  const { run: debounceUpdateSlug } = useDebounceFn(
    (debouncedSlug: typeof slug) => {
      action({
        type: 'UPDATE_TAG_SLUG',
        payload: {
          slug: debouncedSlug,
        },
      });
    },
  );
  const { run: debounceUpdateDescription } = useDebounceFn(
    (debouncedDescription: typeof description) => {
      action({
        type: 'UPDATE_TAG_DESCRIPTION',
        payload: {
          description: debouncedDescription,
        },
      });
    },
  );

  const { categories = [] } = categoriesData ?? {};
  const categoriesTreeData = generateTreeSelectData(
    categories,
    'id',
    'title',
    'id',
  );

  if (!ownerType) return <React.Fragment />;

  return (
    <Row>
      <Col span={24}>
        <Form.Item<FieldType>
          label='عنوان'
          name='title'
          rules={[inputRule.required, inputRule.string]}
        >
          <Input
            placeholder='عنوان برچسب را وارد کنید'
            onChange={({ target: { value } }) => {
              debounceUpdateTitle(value);
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='پیوند یکتا (Slug)'
          name='slug'
          rules={[inputRule.required, inputRule.nonDot]}
        >
          <Input
            placeholder='پیوند یکتا برچسب را وارد کنید'
            onChange={({ target: { value } }) => {
              debounceUpdateSlug(value);
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType> label='دسته‌بندی‌های مرتبط' name='categories'>
          <PrimitiveTreeSelect<TreeSelectSource[] | undefined>
            multiple
            treeCheckable
            treeCheckStrictly
            placeholder='دسته‌بندی‌های مرتبط برچسب را انتخاب کنید'
            loading={categoriesLoading}
            treeData={categoriesTreeData}
            onChange={(value = []) => {
              const selectedCategories = value.map(({ value }) => {
                const otherCategoryId = normalizeToString(value);
                const selectedOtherCategory = findTreeElementByKey(
                  categories,
                  'id',
                  otherCategoryId,
                )!;

                return selectedOtherCategory;
              });

              action({
                type: 'UPDATE_TAG_CATEGORIES',
                payload: {
                  categories: selectedCategories,
                },
              });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType> label='توضیحات' name='description'>
          <RichTextEditor
            ownerType={ownerType}
            options={{
              content: description,
              onUpdate: ({ editor }) => {
                debounceUpdateDescription(editor.getHTML());
              },
            }}
            config={{
              placeholder: {
                placeholder: 'توضیحات برچسب را وارد کنید',
              },
            }}
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

export { TagBaseInfo };
